import React, { useState, useEffect } from 'react';
import { useAdmin } from '../../contexts/AdminContext';
import adminApiService from '../../services/adminApiService';
import toast from 'react-hot-toast';
import {
  EyeIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  ClockIcon,
  FlagIcon,
  DocumentTextIcon,
  ChatBubbleLeftRightIcon,
  PhotoIcon
} from '@heroicons/react/24/outline';

interface ContentItem {
  id: string;
  type: 'job_posting' | 'user_profile' | 'course_content' | 'comment' | 'message';
  title: string;
  content: string;
  reportedBy: string;
  reportReason: string;
  reportedAt: Date;
  status: 'pending' | 'approved' | 'rejected' | 'needs_review';
  authorId: string;
  authorName: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  category: 'spam' | 'inappropriate' | 'harassment' | 'misinformation' | 'other';
}

interface ModerationSummary {
  pending: number;
  approved: number;
  rejected: number;
  totalReports: number;
  highPriority: number;
}

const ContentModeration: React.FC = () => {
  const { adminUser, hasPermission } = useAdmin();
  const [contentItems, setContentItems] = useState<ContentItem[]>([]);
  const [summary, setSummary] = useState<ModerationSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedType, setSelectedType] = useState<string>('all');
  const [selectedStatus, setSelectedStatus] = useState<string>('pending');
  const [selectedSeverity, setSelectedSeverity] = useState<string>('all');

  useEffect(() => {
    fetchContentItems();
  }, [selectedType, selectedStatus, selectedSeverity]);

  const fetchContentItems = async () => {
    try {
      setLoading(true);
      
      const response = await adminApiService.getModerationQueue(
        1, 50, 
        selectedType !== 'all' ? selectedType : undefined,
        selectedStatus !== 'all' ? selectedStatus : undefined,
        selectedSeverity !== 'all' ? selectedSeverity : undefined
      );
      
      if (response.success && response.data) {
        // Transform backend data to frontend format
        const backendItems = response.data.items || [];
        const transformedItems: ContentItem[] = backendItems.map((item: any) => ({
          id: item.id,
          type: item.contentType || 'other',
          title: item.title || `${item.contentType} content`,
          content: item.description || item.content || 'No content available',
          reportedBy: item.reportedBy || 'Unknown',
          reportReason: item.reason || 'No reason provided',
          reportedAt: item.reportedAt ? new Date(item.reportedAt.seconds * 1000) : new Date(),
          status: item.status === 'needs_review' ? 'needs_review' : item.status,
          authorId: item.userId || item.authorId || 'unknown',
          authorName: item.userName || item.authorName || 'Unknown User',
          severity: item.priority || 'medium',
          category: item.category || 'other'
        }));

        const summary: ModerationSummary = {
          pending: response.data.summary?.pending || 0,
          approved: response.data.summary?.approved || 0,
          rejected: response.data.summary?.rejected || 0,
          totalReports: response.data.total || 0,
          highPriority: response.data.summary?.highPriority || 0
        };

        setContentItems(transformedItems);
        setSummary(summary);
      } else {
        throw new Error('Failed to load content moderation data');
      }
    } catch (error: any) {
      console.error('Failed to fetch content items:', error);
      toast.error('Failed to load content moderation data');
    } finally {
      setLoading(false);
    }
  };

  const handleModerationAction = async (contentId: string, action: 'approve' | 'reject', reason?: string) => {
    try {
      const moderationAction = action === 'approve' ? 'approved' : 'rejected';
      const response = await adminApiService.moderateContent(contentId, moderationAction, reason);
      
      if (response.success) {
        toast.success(`Content ${action}d successfully`);
        fetchContentItems(); // Refresh data
      } else {
        throw new Error(response.message || `Failed to ${action} content`);
      }
    } catch (error: any) {
      console.error(`Moderate content error:`, error);
      toast.error(`Failed to ${action} content: ${error.message || 'Unknown error'}`);
    }
  };

  const getTypeIcon = (type: string) => {
    const icons = {
      job_posting: DocumentTextIcon,
      user_profile: EyeIcon,
      course_content: DocumentTextIcon,
      comment: ChatBubbleLeftRightIcon,
      message: ChatBubbleLeftRightIcon
    };
    const Icon = icons[type as keyof typeof icons] || DocumentTextIcon;
    return <Icon className="h-5 w-5" />;
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      pending: { color: 'bg-yellow-100 text-yellow-800', icon: ClockIcon, label: 'Pending' },
      approved: { color: 'bg-green-100 text-green-800', icon: CheckCircleIcon, label: 'Approved' },
      rejected: { color: 'bg-red-100 text-red-800', icon: XCircleIcon, label: 'Rejected' },
      needs_review: { color: 'bg-orange-100 text-orange-800', icon: ExclamationTriangleIcon, label: 'Needs Review' }
    };
    
    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.pending;
    const Icon = config.icon;
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        <Icon className="h-3 w-3 mr-1" />
        {config.label}
      </span>
    );
  };

  const getSeverityBadge = (severity: string) => {
    const severityColors = {
      low: 'bg-gray-100 text-gray-800',
      medium: 'bg-yellow-100 text-yellow-800',
      high: 'bg-orange-100 text-orange-800',
      critical: 'bg-red-100 text-red-800'
    };
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${severityColors[severity as keyof typeof severityColors]}`}>
        {severity.charAt(0).toUpperCase() + severity.slice(1)}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-2xl font-semibold text-gray-900">Content Moderation</h1>
          <p className="mt-2 text-sm text-gray-700">
            Review and moderate reported content across the platform
          </p>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-5">
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <ClockIcon className="h-6 w-6 text-yellow-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Pending Review</dt>
                    <dd className="text-lg font-medium text-gray-900">{summary.pending}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <CheckCircleIcon className="h-6 w-6 text-green-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Approved</dt>
                    <dd className="text-lg font-medium text-gray-900">{summary.approved}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <XCircleIcon className="h-6 w-6 text-red-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Rejected</dt>
                    <dd className="text-lg font-medium text-gray-900">{summary.rejected}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <FlagIcon className="h-6 w-6 text-blue-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Total Reports</dt>
                    <dd className="text-lg font-medium text-gray-900">{summary.totalReports}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <ExclamationTriangleIcon className="h-6 w-6 text-orange-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">High Priority</dt>
                    <dd className="text-lg font-medium text-gray-900">{summary.highPriority}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div>
              <label htmlFor="type" className="block text-sm font-medium text-gray-700">
                Content Type:
              </label>
              <select
                id="type"
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value)}
                className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
              >
                <option value="all">All Types</option>
                <option value="job_posting">Job Postings</option>
                <option value="user_profile">User Profiles</option>
                <option value="course_content">Course Content</option>
                <option value="comment">Comments</option>
                <option value="message">Messages</option>
              </select>
            </div>
            
            <div>
              <label htmlFor="status" className="block text-sm font-medium text-gray-700">
                Status:
              </label>
              <select
                id="status"
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
              >
                <option value="all">All Status</option>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
                <option value="needs_review">Needs Review</option>
              </select>
            </div>

            <div>
              <label htmlFor="severity" className="block text-sm font-medium text-gray-700">
                Severity:
              </label>
              <select
                id="severity"
                value={selectedSeverity}
                onChange={(e) => setSelectedSeverity(e.target.value)}
                className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
              >
                <option value="all">All Severity</option>
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="critical">Critical</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Content Items List */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        <ul className="divide-y divide-gray-200">
          {contentItems.map((item) => (
            <li key={item.id}>
              <div className="px-4 py-4 sm:px-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center min-w-0 flex-1">
                    <div className="flex-shrink-0 mr-4">
                      {getTypeIcon(item.type)}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {item.title}
                        </p>
                        {getStatusBadge(item.status)}
                        {getSeverityBadge(item.severity)}
                      </div>
                      
                      <div className="text-sm text-gray-500 mb-2">
                        <span className="font-medium">Reported by:</span> {item.reportedBy} • 
                        <span className="font-medium"> Author:</span> {item.authorName} • 
                        <span className="font-medium"> Date:</span> {new Date(item.reportedAt).toLocaleDateString()}
                      </div>
                      
                      <div className="text-sm text-gray-600 mb-2">
                        <span className="font-medium">Reason:</span> {item.reportReason}
                      </div>
                      
                      <div className="text-sm text-gray-700">
                        <span className="font-medium">Content:</span> {item.content.substring(0, 100)}...
                      </div>
                      
                      <div className="mt-2">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800`}>
                          {item.category}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="ml-2 flex-shrink-0 flex space-x-2">
                    {item.status === 'pending' && (
                      <>
                        <button
                          onClick={() => handleModerationAction(item.id, 'approve')}
                          className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-full shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                        >
                          <CheckCircleIcon className="h-4 w-4 mr-1" />
                          Approve
                        </button>
                        <button
                          onClick={() => handleModerationAction(item.id, 'reject')}
                          className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-full shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                        >
                          <XCircleIcon className="h-4 w-4 mr-1" />
                          Reject
                        </button>
                      </>
                    )}
                    <button className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded-full text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
                      <EyeIcon className="h-4 w-4 mr-1" />
                      View Details
                    </button>
                  </div>
                </div>
              </div>
            </li>
          ))}
        </ul>
      </div>

      {contentItems.length === 0 && (
        <div className="text-center py-12">
          <FlagIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No content to moderate</h3>
          <p className="mt-1 text-sm text-gray-500">
            No content items match the current filters.
          </p>
        </div>
      )}
    </div>
  );
};

export default ContentModeration;